/**
 * <sky-showcase> — pinned scroll story. While the stage is pinned, each new image is revealed
 * from the bottom (clip-path), the collection list re-centres on the active item and the
 * glass info card slides to the matching description.
 */
class SkyShowcase extends HTMLElement {
  connectedCallback() {
    this.images = [...this.querySelectorAll('[data-image]')];
    this.items = [...this.querySelectorAll('.sky-showcase__list-item')];
    this.cards = [...this.querySelectorAll('.sky-showcase__card-item')];
    this.list = this.querySelector('[data-list]');
    this.banner = this.querySelector('.sky-showcase__banner-bg');
    this.active = -1;
    this.desktop = window.matchMedia('(min-width: 768px)');

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.render();
      });
    };

    // Horizon scrolls `.page-wrapper` on desktop, so listen in the capture phase for any scroller.
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.render();

    this.addEventListener('shopify:block:select', (event) => {
      const index = this.items.indexOf(/** @type {HTMLElement} */ (event.target));
      if (index > -1) this.scrollToIndex(index);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.frame);
  }

  /**
   * Progress of the pinned phase, from rect alone (works for window or `.page-wrapper` scrolling).
   * The phase starts once the stage has covered the banner (1 screen in) and ends 1 screen before the bottom.
   */
  get metrics() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const distance = Math.max(rect.height - 2 * vh, 1);
    const progress = Math.min(Math.max((-rect.top - vh) / distance, 0), 1);
    return { rect, vh, distance, progress };
  }

  render() {
    if (!this.desktop.matches || this.images.length === 0) return;

    const { rect, vh, progress } = this.metrics;

    // Banner fades while the stage slides over it.
    const intro = Math.min(Math.max(-rect.top / vh, 0), 1);
    this.banner?.style.setProperty('--banner-opacity', String(1 - intro * 0.85));

    const steps = this.images.length - 1;
    const position = progress * steps;

    this.images.forEach((image, i) => {
      if (i === 0) return;
      const reveal = Math.min(Math.max(position - (i - 1), 0), 1);
      image.style.clipPath = `inset(${((1 - reveal) * 100).toFixed(2)}% 0 0 0)`;
      image.style.setProperty('--parallax', `${((1 - reveal) * 48).toFixed(1)}px`);
    });

    this.setActive(Math.min(Math.round(position), steps));
  }

  /** @param {number} index */
  setActive(index) {
    if (index === this.active) return;
    this.active = index;

    this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    this.style.setProperty('--active', String(index));

    const item = this.items[index];
    if (item && this.list) {
      const offset = item.offsetTop + item.offsetHeight / 2 - this.list.offsetHeight / 2;
      this.list.style.setProperty('--list-offset', `${-offset}px`);
    }
  }

  /** @param {number} index */
  scrollToIndex(index) {
    const { rect, vh, distance } = this.metrics;
    const steps = this.images.length - 1;
    const targetTop = -(vh + (steps > 0 ? (distance * index) / steps : 0));
    const wrapper = document.querySelector('.page-wrapper');
    const scroller = wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;
    scroller.scrollBy({ top: rect.top - targetTop, behavior: 'smooth' });
  }
}

if (!customElements.get('sky-showcase')) customElements.define('sky-showcase', SkyShowcase);
